/**
 * Mock search service.
 * Mirrors the real search.service.js API with in-memory data.
 */
import { MOCK_USERS } from '../../data/mock/mock-users.data';
import { MOCK_POSTS } from '../../data/mock/mock-posts.data';
import { MOCK_TAGS } from '../../data/mock/mock-tags.data';
import { mockDelay, mockLog } from './mock-delay';

const RECENT_SEARCHES_KEY = 'fitsocial_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// ── Users search ────────────────────────────────────────────────────────────

export const searchUsers = async (queryStr, resultLimit = 10) => {
  mockLog('SearchService', 'searchUsers', queryStr);
  await mockDelay();
  const q = queryStr.toLowerCase().trim();
  if (!q) return [];
  return MOCK_USERS
    .filter(
      (u) =>
        u.username.includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        (u.bio || '').toLowerCase().includes(q)
    )
    .slice(0, resultLimit);
};

// ── Posts search ─────────────────────────────────────────────────────────────

const paginate = (items, lastDoc, pageLimit) => {
  let startIndex = 0;
  if (lastDoc) {
    const idx = items.findIndex((p) => p.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }
  const page = items.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < items.length;
  return { page, lastDoc: newLastDoc, hasMore };
};

export const searchPosts = async (queryStr, resultLimit = 10, lastDoc = null) => {
  mockLog('SearchService', 'searchPosts', queryStr);
  await mockDelay();
  const q = queryStr.toLowerCase().trim();
  if (!q) return { posts: [], lastDoc: null, hasMore: false };

  const filtered = MOCK_POSTS
    .filter(
      (p) =>
        (p.text || '').toLowerCase().includes(q) ||
        (p.authorName || '').toLowerCase().includes(q) ||
        (p.authorUsername || '').toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.includes(q))
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, resultLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

// ── Workouts search ──────────────────────────────────────────────────────────

export const searchWorkouts = async (queryStr, userId = null, resultLimit = 10) => {
  mockLog('SearchService', 'searchWorkouts', queryStr);
  await mockDelay();
  const q = queryStr.toLowerCase().trim();
  if (!q) return [];

  return MOCK_POSTS
    .filter((p) => {
      if (p.type !== 'workout') return false;
      if (userId && p.authorId !== userId) return false;
      const title = (p.workout?.title || '').toLowerCase();
      const text = (p.text || '').toLowerCase();
      return title.includes(q) || text.includes(q);
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, resultLimit);
};

// ── Tags ─────────────────────────────────────────────────────────────────────

export const searchByTag = async (tag, resultLimit = 10, lastDoc = null) => {
  mockLog('SearchService', 'searchByTag', tag);
  await mockDelay();
  const normalized = tag.toLowerCase().trim();
  if (!normalized) return { posts: [], lastDoc: null, hasMore: false };

  const filtered = MOCK_POSTS
    .filter((p) => (p.tags || []).includes(normalized))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, resultLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getTrendingTags = async (resultLimit = 12) => {
  mockLog('SearchService', 'getTrendingTags');
  await mockDelay();
  return [...MOCK_TAGS]
    .sort((a, b) => b.postCount - a.postCount)
    .slice(0, resultLimit);
};

export const searchTags = async (queryStr, resultLimit = 10) => {
  mockLog('SearchService', 'searchTags', queryStr);
  await mockDelay();
  const q = queryStr.toLowerCase().trim();
  if (!q) return [];

  return MOCK_TAGS
    .filter((t) => t.name.includes(q))
    .sort((a, b) => b.postCount - a.postCount)
    .slice(0, resultLimit);
};

export const incrementTagCounts = async (tags) => {
  mockLog('SearchService', 'incrementTagCounts', tags);
  // No-op in mock mode
};

export const decrementTagCounts = async (tags) => {
  mockLog('SearchService', 'decrementTagCounts', tags);
  // No-op in mock mode
};

// ── Recent searches (localStorage) ──────────────────────────────────────────

export const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveRecentSearch = (queryStr) => {
  try {
    const trimmed = queryStr.trim();
    if (!trimmed) return;
    const recent = getRecentSearches().filter((s) => s !== trimmed);
    recent.unshift(trimmed);
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES))
    );
  } catch {
    // Ignore
  }
};

export const clearRecentSearches = () => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Ignore
  }
};
